import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  Network,
  DollarSign,
  CreditCard,
  Package,
  BarChart3,
  Settings,
  X,
  LogOut,
  ChevronRight,
  Sparkles,
  ShoppingBag,
  ListOrdered,
  GitBranch,
  Store,
  Award,
  Wallet,
  Globe,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';

const AdminSidebar = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const adminNavigation = [
    { section: 'General' },
    {
      name: t('common.dashboard'),
      href: '/admin',
      icon: LayoutDashboard,
      description: t('admin.systemOverview')
    },
    {
      name: t('common.network'),
      href: '/admin/network',
      icon: Network,
      description: t('admin.networkStructure')
    },
    { section: 'Usuarios y Comisiones' },
    {
      name: t('common.users'),
      href: '/admin/users',
      icon: Users,
      description: t('admin.memberManagement')
    },
    {
      name: t('common.commissions'),
      href: '/admin/commissions',
      icon: DollarSign,
      description: t('admin.paymentSettings')
    },
    {
      name: t('common.payouts'),
      href: '/admin/payouts',
      icon: CreditCard,
      description: t('admin.requestsApprovals')
    },
    { section: 'MLM' },
    {
      name: 'Red MLM',
      href: '/admin/mlm',
      icon: GitBranch,
      description: 'Gestión de red y rangos'
    },
    { section: 'Catálogo' },
    {
      name: t('common.products'),
      href: '/admin/products',
      icon: Package,
      description: t('admin.catalogPrices')
    },
    {
      name: 'Categorías',
      href: '/admin/categories',
      icon: ShoppingBag,
      description: 'Gestión de categorías'
    },
    {
      name: 'Subcategorías',
      href: '/admin/subcategories',
      icon: ShoppingBag,
      description: 'Gestión de subcategorías'
    },
    {
      name: 'Familias',
      href: '/admin/families',
      icon: ShoppingBag,
      description: 'Gestión de familias'
    },
    {
      name: 'Packs',
      href: '/admin/packs',
      icon: Package,
      description: 'Gestión de Sttora Packs'
    },
    {
      name: 'Órdenes',
      href: '/admin/orders',
      icon: ListOrdered,
      description: 'Gestión de pedidos'
    },
    { section: 'Analítica' },
    {
      name: t('common.reports'),
      href: '/admin/reports',
      icon: BarChart3,
      description: t('admin.advancedAnalytics')
    },
    {
      name: t('common.settings'),
      href: '/admin/settings',
      icon: Settings,
      description: t('admin.systemSettings')
    },
  ];

  const userNavigation = [
    { section: 'General' },
    {
      name: t('common.dashboard'),
      href: '/dashboard',
      icon: LayoutDashboard,
      description: t('user.myMainPanel')
    },
    {
      name: t('user.myNetwork'),
      href: '/network',
      icon: Network,
      description: t('user.myNetworkStructure')
    },
    { section: 'MLM' },
    {
      name: 'Dashboard MLM',
      href: '/mlm',
      icon: GitBranch,
      description: 'Red y estadísticas MLM'
    },
    {
      name: 'Árbol Binario',
      href: '/mlm/binary-tree',
      icon: Network,
      description: 'Visualización de red binaria'
    },
    {
      name: 'Árbol Unilevel',
      href: '/mlm/unilevel-tree',
      icon: Users,
      description: 'Línea descendente unilevel'
    },
    {
      name: 'Progresión de Rango',
      href: '/mlm/rank-progression',
      icon: Award,
      description: 'Rangos y logros'
    },
    {
      name: 'Wallet',
      href: '/mlm/wallet',
      icon: Wallet,
      description: 'Saldo y comisiones'
    },
    {
      name: 'Sttora Points',
      href: '/mlm/sttora-points',
      icon: Globe,
      description: 'Puntos y recompensas'
    },
    {
      name: 'Global Partner Pool',
      href: '/mlm/global-pool',
      icon: DollarSign,
      description: 'Pool global de socios'
    },
    { section: 'Finanzas' },
    {
      name: t('common.commissions'),
      href: '/commissions',
      icon: DollarSign,
      description: t('user.myEarnings')
    },
    { section: 'Tienda' },
    {
      name: 'Tienda STTORA',
      href: '/store',
      icon: Store,
      description: 'Catálogo de productos'
    },
    {
      name: 'Mis Órdenes',
      href: '/orders',
      icon: ListOrdered,
      description: 'Historial de pedidos'
    },
    { section: 'Cuenta' },
    {
      name: t('common.profile'),
      href: '/profile',
      icon: Settings,
      description: t('user.myPersonalInfo')
    },
  ];

  const navigation = user?.role === 'admin' ? adminNavigation : userNavigation;

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    }
  };

  return (
    <>
      {/* Overlay para mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 lg:hidden z-40"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        variants={sidebarVariants}
        initial="closed"
        animate={isOpen ? "open" : "closed"}
        className={`fixed inset-y-0 left-0 z-50 w-80 ${isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-xl border-r ${isDarkMode ? 'border-gray-700/20' : 'border-gray-200/20'} shadow-2xl`}
      >
        {/* Header del Sidebar */}
        <div className="flex flex-col h-full">
          <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? 'border-gray-700/20' : 'border-gray-200/20'}`}>
            <motion.div
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h1 className={`text-xl font-bold bg-gradient-to-r ${isDarkMode ? 'from-gray-100 to-gray-300' : 'from-gray-900 to-gray-600'} bg-clip-text text-transparent`}>
                  Sttora
                </h1>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('admin.controlPanel')}</p>
              </div>
            </motion.div>

            <button
              onClick={onClose}
              className={`lg:hidden p-2 hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-xl transition-colors`}
            >
              <X className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>
          </div>

          {/* Navegación */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto custom-scrollbar">
            {navigation.map((item, index) => {
              if (item.section) {
                return (
                  <div key={item.section} className={`px-3 pt-4 pb-2 text-[10px] uppercase tracking-widest font-semibold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {item.section}
                  </div>
                );
              }
              return (
                <motion.div
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className={`flex items-center justify-between p-3 rounded-2xl ${isDarkMode ? 'text-gray-300 hover:bg-gradient-to-r hover:from-orange-900/50 hover:to-orange-800/50 hover:text-orange-400' : 'text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600'} group transition-all duration-200 cursor-pointer`}
                  whileHover={{
                    x: 8,
                    transition: { duration: 0.2 }
                  }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{
                    opacity: 1,
                    x: 0,
                    transition: { delay: index * 0.1 }
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm group-hover:shadow-md transition-shadow`}>
                      <item.icon className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{item.name}</div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.description}</div>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-gray-500 group-hover:text-orange-400' : 'text-gray-400 group-hover:text-orange-500'} transform group-hover:translate-x-1 transition-transform`} />
                </motion.div>
              );
            })}
          </nav>

          {/* Footer del Sidebar */}
          <div className={`p-6 border-t ${isDarkMode ? 'border-gray-700/20' : 'border-gray-200/20'}`}>
            <motion.div
              className={`flex items-center space-x-3 p-3 rounded-2xl bg-gradient-to-r ${isDarkMode ? 'from-orange-900/20 to-orange-800/20' : 'from-orange-500/10 to-orange-600/10'}`}
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white font-bold text-sm">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} truncate`}>
                  {user?.name || 'Administrador'}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} truncate`}>{t('admin.mainAdmin')}</p>
              </div>
              <motion.button
                onClick={() => { logout(); navigate('/login'); }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`p-2 ${isDarkMode ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'} rounded-lg transition-colors`}
              >
                <LogOut className="w-4 h-4" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default AdminSidebar;